import { FC } from 'react'
import styled, { keyframes } from 'styled-components'

interface TipsListSkeletonProps {
  count?: number
}

const TipsListSkeleton: FC<TipsListSkeletonProps> = ({ count = 3 }) => {
  return (
    <SkeletonContainer>
      <SkeletonHeader>
        <SkeletonBrandIcon />
        <SkeletonTitle />
      </SkeletonHeader>

      {Array.from({ length: count }).map((_, index) => (
        <SkeletonTipCard key={index}>
          <SkeletonCardHeader>
            <SkeletonTipId />
            <SkeletonTipType />
            <SkeletonVipBadge />
            <SkeletonBookmarkIcon />
          </SkeletonCardHeader>

          <SkeletonMatchSection>
            <SkeletonTeam>
              <SkeletonTeamLogo />
              <SkeletonTeamName width="45%" />
            </SkeletonTeam>

            <SkeletonScoreContainer>
              <SkeletonScore />
              <SkeletonColon />
              <SkeletonScore />
            </SkeletonScoreContainer>

            <SkeletonTeam>
              <SkeletonTeamLogo />
              <SkeletonTeamName width="45%" />
            </SkeletonTeam>
          </SkeletonMatchSection>

          <SkeletonWinnerSection>
            <SkeletonWinnerLabel />
            <SkeletonWinnerValue />
          </SkeletonWinnerSection>

          <SkeletonAnalysisSection>
            <SkeletonAnalysisLabel />
            <SkeletonAnalysisText />
          </SkeletonAnalysisSection>

          <SkeletonCardFooter>
            <SkeletonVoteSection>
              <SkeletonVoteButton />
              <SkeletonVoteCount />
              <SkeletonVoteButton />
            </SkeletonVoteSection>
            <SkeletonCommentCount />
            <SkeletonShowMore />
          </SkeletonCardFooter>
        </SkeletonTipCard>
      ))}
    </SkeletonContainer>
  )
}

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`

const SkeletonBase = styled.div`
  background: linear-gradient(90deg, var(--zinc-800) 25%, var(--zinc-700) 50%, var(--zinc-800) 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 4px;

  [data-theme="light"] & {
    background: linear-gradient(90deg, var(--border-primary) 25%, var(--bg-tertiary) 50%, var(--border-primary) 75%);
    background-size: 200px 100%;
  }
`

const SkeletonContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 0;
  background: var(--bg-primary);
  min-height: 100vh;
`

const SkeletonHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: var(--standard-header-height, 111px);
  padding: 0 10px;
  border-bottom-right-radius: 20px;
  border-bottom-left-radius: 20px;
  background: var(--bg-tertiary);
  position: relative;
  margin-bottom: 16px;

`

const SkeletonBrandIcon = styled(SkeletonBase)`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  position: relative;
  z-index: 1;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.1) 25%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.1) 75%
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;

  [data-theme="light"] & {
    background: linear-gradient(90deg, var(--border-primary) 25%, var(--bg-tertiary) 50%, var(--border-primary) 75%);
    background-size: 200px 100%;
  }
`

const SkeletonTitle = styled(SkeletonBase)`
  width: 120px;
  height: 20px;
  position: relative;
  z-index: 1;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.1) 25%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.1) 75%
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;

  [data-theme="light"] & {
    background: linear-gradient(90deg, var(--border-primary) 25%, var(--bg-tertiary) 50%, var(--border-primary) 75%);
    background-size: 200px 100%;
  }
`

const SkeletonTipCard = styled.div`
  background: var(--bg-tertiary);
  border-radius: 16px;
  margin: 0 10px 10px;
  padding: 10px;
  border: 1px solid var(--zinc-800);

  [data-theme="light"] & {
    border: 1px solid var(--border-primary);
  }
`

const SkeletonCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`

const SkeletonTipId = styled(SkeletonBase)`
  width: 60px;
  height: 24px;
`

const SkeletonTipType = styled(SkeletonBase)`
  width: 80px;
  height: 24px;
`

const SkeletonVipBadge = styled(SkeletonBase)`
  width: 48px;
  height: 24px;
  border-radius: 12px;
`

const SkeletonBookmarkIcon = styled(SkeletonBase)`
  width: 24px;
  height: 24px;
  margin-left: auto;
`

const SkeletonMatchSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
`

const SkeletonTeam = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
`

const SkeletonTeamLogo = styled(SkeletonBase)`
  width: 64px;
  height: 64px;
  border-radius: 8px;
`

const SkeletonTeamName = styled(SkeletonBase)<{ width?: string }>`
  width: ${({ width }) => width || '100%'};
  height: 16px;
`

const SkeletonScoreContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SkeletonScore = styled(SkeletonBase)`
  width: 32px;
  height: 40px;
  border-radius: 8px;
`

const SkeletonColon = styled(SkeletonBase)`
  width: 8px;
  height: 20px;
`

const SkeletonWinnerSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid var(--zinc-800);
  border-bottom: 1px solid var(--zinc-800);
  margin-bottom: 16px;

  [data-theme="light"] & {
    border-top: 1px solid var(--border-primary);
    border-bottom: 1px solid var(--border-primary);
  }
`

const SkeletonWinnerLabel = styled(SkeletonBase)`
  width: 100px;
  height: 16px;
`

const SkeletonWinnerValue = styled(SkeletonBase)`
  width: 60px;
  height: 20px;
`

const SkeletonAnalysisSection = styled.div`
  margin-bottom: 16px;
`

const SkeletonAnalysisLabel = styled(SkeletonBase)`
  width: 80px;
  height: 14px;
  margin-bottom: 8px;
`

const SkeletonAnalysisText = styled(SkeletonBase)`
  width: 100%;
  height: 16px;
`

const SkeletonCardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--zinc-800);

  [data-theme="light"] & {
    border-top: 1px solid var(--border-primary);
  }
`

const SkeletonVoteSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SkeletonVoteButton = styled(SkeletonBase)`
  width: 32px;
  height: 32px;
  border-radius: 4px;
`

const SkeletonVoteCount = styled(SkeletonBase)`
  width: 24px;
  height: 20px;
`

const SkeletonCommentCount = styled(SkeletonBase)`
  width: 48px;
  height: 20px;
`

const SkeletonShowMore = styled(SkeletonBase)`
  width: 100px;
  height: 24px;
  margin-left: auto;
  border-radius: 4px;
`

export default TipsListSkeleton
